/**
 * The Eaze sound — a short rising chime made in the browser (no audio file
 * to download): three glassy notes E5 → B5 → E6, the "E" of Eaze up an
 * octave, ~0.6 s. "attention" (approvals, things waiting on you) plays it
 * twice, a touch brighter. Quiet by design.
 *
 * Browsers only allow sound after the person has touched the page once, so
 * the audio is unlocked on the first tap/key. The on/off choice is per
 * device (localStorage, a convenience — it never fails the page).
 */
type Kind = "notify" | "attention";

const KEY = "eaze.sound";
let ctx: AudioContext | null = null;
let lastAt = 0;

export function soundOn(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundOn(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* private mode: stays on for this visit */
  }
}

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  return ctx;
}

/** Call once at start: the first tap or key press unlocks sound. */
export function unlockSoundOnFirstGesture() {
  if (typeof window === "undefined") return;
  const go = () => {
    const a = audio();
    if (a && a.state === "suspended") void a.resume();
    window.removeEventListener("pointerdown", go);
    window.removeEventListener("keydown", go);
  };
  window.addEventListener("pointerdown", go, { once: true });
  window.addEventListener("keydown", go, { once: true });
}

function note(a: AudioContext, freq: number, at: number, dur: number, peak: number, out: AudioNode) {
  // A sine body with a soft triangle shimmer an octave up: bell-like, not beepy.
  for (const [type, mult, gain] of [["sine", 1, 1], ["triangle", 2, 0.18]] as const) {
    const o = a.createOscillator();
    const g = a.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq * mult, at);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak * gain, at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    o.connect(g).connect(out);
    o.start(at);
    o.stop(at + dur + 0.05);
  }
}

/** Play the Eaze chime (no-op when sound is off, locked, or just played). */
export function playEazeSound(kind: Kind = "notify", { force = false } = {}) {
  if (!force && !soundOn()) return false;
  const a = audio();
  if (!a || a.state !== "running") return false;
  const now = a.currentTime;
  if (!force && performance.now() - lastAt < 1500) return false; // a burst sounds once
  lastAt = performance.now();
  const master = a.createGain();
  master.gain.value = 0.16;
  const tone = a.createBiquadFilter();
  tone.type = "lowpass";
  tone.frequency.value = kind === "attention" ? 7000 : 5200;
  master.connect(tone).connect(a.destination);
  const notes = [659.25, 987.77, 1318.51]; // E5, B5, E6
  const play = (start: number) => notes.forEach((f, i) => note(a, f, start + i * 0.075, i === 2 ? 0.55 : 0.32, 0.9 - i * 0.15, master));
  play(now + 0.01);
  if (kind === "attention") play(now + 0.42);
  return true;
}
